'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import {
  BottomSheetHandleArea,
  BottomSheetTitle,
  BottomSheetTitleBox,
  BottomSheetTitleContainer,
  Button,
  Dim,
  IcDelete,
  IconButton,
  Sheet,
  SheetContent,
  SheetDimMotion,
  SheetMotion,
  SheetPortal,
  SheetTrigger,
  Typography,
  type TypographyProps,
} from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQuery } from '@tanstack/react-query';
import type { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import { getPaths } from '../../common';
import { useNavigate } from '../../common/hooks/useNavigate';
import { useGoProfileEdit } from '../../common/paths/actions';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';

const OptionItem: FC<TypographyProps> = ({ className, ...props }) => {
  return (
    <Typography
      className={twMerge('px-[16px]! py-[12px]! cursor-pointer', className)}
      colorScheme="gray-600"
      variant="body13pxReg"
      {...props}
    />
  );
};

type Props = {
  className?: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export const MyShopProfileEditBottomSheetButton = ({ className, open, onOpen, onClose }: Props) => {
  const { navigate } = useNavigate();
  const { service } = useDeviceInfo();
  const paths = getPaths(service);
  const goProfileEdit = useGoProfileEdit();

  const userInfoQuery = useQuery({
    ...curatorQueries.profile.userInfo,
  });

  return (
    <Sheet direction="bottom" open={open}>
      <SheetTrigger asChild onClick={onOpen}>
        <Typography size="13" weight="medium" asChild>
          <Button
            variant="secondary"
            size="32"
            className={className}
            onClick={() => {
              if (service === SERVICE['29CM']) {
                track({
                  schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
                  data: {
                    page_name: 'curator_shop_manage',
                    section_name: 'CurationShopInfo',
                    section_title: 'CurationShopInfo',
                    button_id: 'edit_my_info',
                    button_name: '프로필 수정',
                    position: -1,
                  },
                });
              }
              if (service === SERVICE.MUSINSA) {
                amplitudeSendEvent({
                  action_name: 'click',
                  button_name: 'profile_edit_try',
                  component_name: 'button',
                  event_name: 'click_button',
                  from: getScreenName(),
                  id: userInfoQuery.data?.snsTitle ?? '',
                  section_name: 'curator_profile',
                });
              }
            }}
          >
            프로필 수정
          </Button>
        </Typography>
      </SheetTrigger>
      <SheetPortal>
        <SheetContent onPointerDownOutside={onClose}>
          <SheetMotion direction="bottom">
            <BottomSheetTitle>
              <BottomSheetHandleArea />
              <BottomSheetTitleContainer>
                <BottomSheetTitleBox>프로필 수정</BottomSheetTitleBox>
                <IconButton size="size28x28" onClick={onClose}>
                  <IcDelete />
                </IconButton>
              </BottomSheetTitleContainer>
            </BottomSheetTitle>
            <div className="flex flex-col bg-white safe-area-bottom">
              <OptionItem
                className="pb-[12px]! pt-[4px]!"
                onClick={() => {
                  if (service === SERVICE.MUSINSA) {
                    amplitudeSendEvent({
                      action_name: 'click',
                      button_name: 'profile_edit_select',
                      component_name: 'button',
                      event_name: 'click_button',
                      from: getScreenName(),
                      id: userInfoQuery.data?.snsTitle ?? '',
                      name: 'profile',
                      section_name: 'curator_profile',
                    });
                  }
                  goProfileEdit();
                }}
              >
                프로필 이미지 및 닉네임
              </OptionItem>
              <OptionItem
                onClick={() => {
                  if (service === SERVICE.MUSINSA) {
                    amplitudeSendEvent({
                      action_name: 'click',
                      button_name: 'profile_edit_select',
                      component_name: 'button',
                      event_name: 'click_button',
                      from: getScreenName(),
                      id: userInfoQuery.data?.snsTitle ?? '',
                      name: 'shop_description',
                      section_name: 'curator_profile',
                    });
                  }
                  navigate(paths.curationInfo());
                }}
              >
                큐레이션 샵 소개
              </OptionItem>
              <OptionItem
                onClick={() => {
                  if (service === SERVICE.MUSINSA) {
                    amplitudeSendEvent({
                      action_name: 'click',
                      button_name: 'profile_edit_select',
                      component_name: 'button',
                      event_name: 'click_button',
                      from: getScreenName(),
                      id: userInfoQuery.data?.snsTitle ?? '',
                      name: 'link',
                      section_name: 'curator_profile',
                    });
                    if (!userInfoQuery.data?.snsTitle) return;
                  }
                  navigate(paths.profileSns({ shopSlug: userInfoQuery.data?.snsTitle ?? '' }));
                }}
              >
                SNS 링크
              </OptionItem>
            </div>
          </SheetMotion>
        </SheetContent>

        <SheetDimMotion>
          <Dim />
        </SheetDimMotion>
      </SheetPortal>
    </Sheet>
  );
};
